<template>
    <Chart
        :height="400"
        :series="chartSeries"
        :options="chartOptions"
        :loading="request.getLoading() || requestCompare.getLoading()"
        card
        class="m-0"
        :header="$t('averageAvailabilityByProducts')"
    >
        <template #nav>
            <div class="d-flex gap-3">
                <Multiselect
                    :value="quantityToShow"
                    :options="quantityToShowOptions"
                    :placeholder="$t('show')"
                    track-by="value"
                    label="text"
                    select-label=""
                    :disabled="request.getLoading() || requestCompare.getLoading()"
                    class="width-200"
                    @changeTrackBy="quantityToShow = $event"
                />
                <Multiselect
                    :value="sortBy"
                    :options="sortOptions"
                    :placeholder="$t('sortBy')"
                    track-by="value"
                    label="text"
                    select-label=""
                    :disabled="request.getLoading() || requestCompare.getLoading()"
                    class="width-200"
                    @changeTrackBy="sortBy = $event"
                />
            </div>
        </template>
    </Chart>
</template>

<script>
    import Chart from '@/components/common/Chart.vue';
    import Multiselect from '@/components/common/Multiselect.vue';
    import { calcPercentDiff } from '@/utils/number';
    import sum from 'lodash/sum';

    export default {
        name: 'AverageAvailabilityOfProductsByStores',
        components: {
            Chart,
            Multiselect,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestCompare: this.parentComponent.requestDataCompare,
                sortBy: 'summary-desc',
                quantityToShow: 10,
            };
        },
        computed: {
            quantityToShowOptions() {
                return [
                    { value: 10, text: 10 },
                    { value: 20, text: 20 },
                    { value: 50, text: 50 },
                    { value: null, text: this.$t('all') },
                ];
            },
            sortOptions() {
                return [
                    { value: 'summary-desc', text: this.$t('averageDescending') },
                    { value: 'summary-asc', text: this.$t('averageAscending') },
                    { value: 'change-desc', text: this.$t('changeToPreviousRangeDescending') },
                    { value: 'change-asc', text: this.$t('changeToPreviousRangeAscending') },
                ];
            },
            data() {
                return this.request.getData() || {};
            },
            dataCompare() {
                return this.requestCompare.getData() || {};
            },
            chartData() {
                return this.computeChartData(this.data);
            },
            chartDataCompare() {
                return this.computeChartData(this.dataCompare);
            },
            sortedChartData() {
                const compareDict = {};
                this.chartDataCompare.forEach(item => {
                    compareDict[item.product] = item.avgAvailability;
                });
                const dataWithDiff = this.chartData.map(item => {
                    const compareValue = compareDict[item.product] || 0;
                    const diff = calcPercentDiff(item.avgAvailability, compareValue);
                    return { ...item, diff };
                });
                let sorted;
                switch (this.sortBy) {
                    case 'summary-desc':
                        sorted = [...dataWithDiff].sort((a, b) => b.avgAvailability - a.avgAvailability);
                        break;
                    case 'summary-asc':
                        sorted = [...dataWithDiff].sort((a, b) => a.avgAvailability - b.avgAvailability);
                        break;
                    case 'change-desc':
                        sorted = [...dataWithDiff].sort((a, b) => b.diff - a.diff);
                        break;
                    case 'change-asc':
                        sorted = [...dataWithDiff].sort((a, b) => a.diff - b.diff);
                        break;
                    default:
                        sorted = dataWithDiff;
                }
                return this.quantityToShow ? sorted.slice(0, this.quantityToShow) : sorted;
            },
            chartSeries() {
                return [
                    {
                        name: '',
                        data: this.sortedChartData.map(item => item.avgAvailability),
                    },
                ];
            },
            pointsAnnotations() {
                return this.sortedChartData.map(item => {
                    const diff = item.diff;
                    const diffPercent = this.$format.percent(diff);
                    const color = diff === 0 ? 'gray' : diff > 0 ? 'green' : 'red'; // eslint-disable-line no-nested-ternary
                    const triangleSymbol = diff === 0 ? '' : diff > 0 ? '▲' : '▼'; // eslint-disable-line no-nested-ternary
                    return {
                        x: item.avgAvailability,
                        y: item.product,
                        marker: {
                            fillColor: '#fff',
                            strokeColor: color,
                            radius: 2,
                        },
                        label: {
                            text: `${triangleSymbol} ${diffPercent}`,
                            borderColor: color,
                            style: {
                                color: '#fff',
                                background: color,
                            },
                            offsetX: 0,
                            offsetY: 0,
                        },
                    };
                });
            },
            chartOptions() {
                const summary = sum(this.chartSeries[0].data);
                const categories = this.sortedChartData.map(item => item.product);
                const minHeight = 300;
                const itemHeight = 30;
                const titleHeight = 21;
                const gapHeight = 70;
                return {
                    series: this.chartSeries,
                    chart: {
                        type: 'bar',
                        height: this.sortedChartData.length ? this.sortedChartData.length * itemHeight + gapHeight + titleHeight : minHeight,
                    },
                    plotOptions: {
                        bar: {
                            horizontal: true,
                        },
                    },
                    xaxis: {
                        type: 'category',
                        categories,
                        tickAmount: 10,
                    },
                    dataLabels: {
                        enabled: true,
                        formatter: val => `${this.$format.percent((val * 100) / summary, 2)} (${val})`,
                    },
                    tooltip: {
                        y: {
                            formatter: val => `${this.$format.percent((val * 100) / summary, 2)} (${val})`,
                        },
                    },
                    annotations: {
                        points: this.pointsAnnotations,
                    },
                };
            },
        },
        methods: {
            computeChartData(source) {
                /* eslint-disable no-restricted-syntax, guard-for-in */
                const eventsByProduct = {};
                Object.values(source).forEach(productObj => {
                    const productName = productObj.product.name;
                    if (!eventsByProduct[productName]) {
                        eventsByProduct[productName] = [];
                    }
                    const charts = productObj.charts;
                    Object.keys(charts).forEach(chartKey => {
                        charts[chartKey].forEach(chartObj => {
                            const segments = chartObj.chart.segments;
                            if (segments && segments.length) {
                                segments.forEach(segment => {
                                    eventsByProduct[productName].push({ time: segment.start, delta: +1 });
                                    eventsByProduct[productName].push({ time: segment.end, delta: -1 });
                                });
                            }
                        });
                    });
                });
                const productSummaries = [];
                for (const product in eventsByProduct) {
                    const events = eventsByProduct[product];
                    const grouped = {};
                    events.forEach(e => {
                        grouped[e.time] = (grouped[e.time] || 0) + e.delta;
                    });
                    const times = Object.keys(grouped)
                        .map(t => Number(t))
                        .sort((a, b) => a - b);
                    if (times.length === 0) continue; // eslint-disable-line no-continue
                    let cumulative = 0;
                    let weightedSum = 0;
                    let totalDuration = 0;
                    let prevTime = times[0];
                    times.forEach(time => {
                        const duration = time - prevTime;
                        weightedSum += cumulative * duration;
                        totalDuration += duration;
                        cumulative += grouped[time];
                        prevTime = time;
                    });
                    const avg = totalDuration ? weightedSum / totalDuration : cumulative;
                    productSummaries.push({
                        product,
                        avgAvailability: Math.round(avg),
                    });
                }
                productSummaries.sort((a, b) => b.avgAvailability - a.avgAvailability);
                return productSummaries;
                /* eslint-enable no-restricted-syntax, guard-for-in */
            },
        },
    };
</script>
